import { Search, Download, Filter, X, AlertTriangle, Tag, Clock, Package } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

interface OrderFiltersProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  fulfilmentFilter: string;
  onFulfilmentChange: (value: string) => void;
  dateRange: string;
  onDateRangeChange: (value: string) => void;
  onExportCSV: () => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  // Pipeline counts for quick filters
  pipelineCounts?: {
    readyToShip: number;
    needsTracking: number;
    overdue: number;
  };
}

export function OrderFilters({
  searchQuery,
  onSearchChange,
  fulfilmentFilter,
  onFulfilmentChange,
  dateRange,
  onDateRangeChange,
  onExportCSV,
  onClearFilters,
  hasActiveFilters,
  pipelineCounts,
}: OrderFiltersProps) {
  return (
    <div className="space-y-4 mb-6">
      {/* Pipeline Quick Filters */}
      {pipelineCounts && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant={fulfilmentFilter === "ready_to_ship" ? "default" : "outline"}
            size="sm"
            onClick={() => onFulfilmentChange(fulfilmentFilter === "ready_to_ship" ? "all" : "ready_to_ship")}
            className={
              fulfilmentFilter === "ready_to_ship"
                ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                : "border-slate-700 text-slate-300 hover:bg-slate-800"
            }
          >
            <Package className="h-4 w-4 mr-2" />
            Ready to Ship
            <Badge variant="secondary" className="ml-2 bg-slate-700 text-slate-200">
              {pipelineCounts.readyToShip}
            </Badge>
          </Button>
          <Button
            variant={fulfilmentFilter === "needs_tracking" ? "default" : "outline"}
            size="sm"
            onClick={() => onFulfilmentChange(fulfilmentFilter === "needs_tracking" ? "all" : "needs_tracking")}
            className={
              fulfilmentFilter === "needs_tracking"
                ? "bg-blue-600 hover:bg-blue-700 text-white"
                : "border-slate-700 text-slate-300 hover:bg-slate-800"
            }
          >
            <Tag className="h-4 w-4 mr-2" />
            Needs Tracking
            <Badge variant="secondary" className="ml-2 bg-slate-700 text-slate-200">
              {pipelineCounts.needsTracking}
            </Badge>
          </Button>
          <Button
            variant={fulfilmentFilter === "overdue" ? "default" : "outline"}
            size="sm"
            onClick={() => onFulfilmentChange(fulfilmentFilter === "overdue" ? "all" : "overdue")}
            className={
              fulfilmentFilter === "overdue"
                ? "bg-red-600 hover:bg-red-700 text-white"
                : "border-slate-700 text-slate-300 hover:bg-slate-800"
            }
          >
            <AlertTriangle className="h-4 w-4 mr-2" />
            Overdue (3+ days)
            <Badge variant="secondary" className="ml-2 bg-slate-700 text-slate-200">
              {pipelineCounts.overdue}
            </Badge>
          </Button>
        </div>
      )}

      {/* Main Filters Row */}
      <div className="flex flex-col md:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input
            placeholder="Search by email, order ID, or customer name..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 bg-slate-900/70 border-slate-700 text-slate-100 placeholder:text-slate-500"
          />
        </div>

        {/* Fulfilment Filter */}
        <Select value={fulfilmentFilter} onValueChange={onFulfilmentChange}>
          <SelectTrigger className="w-full md:w-[180px] bg-slate-900/70 border-slate-700 text-slate-100">
            <Filter className="h-4 w-4 mr-2 text-slate-500" />
            <SelectValue placeholder="Fulfilment" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="all" className="text-slate-100">
              All Orders
            </SelectItem>
            <SelectItem value="unfulfilled" className="text-slate-100">
              Unfulfilled
            </SelectItem>
            <SelectItem value="fulfilled" className="text-slate-100">
              Fulfilled
            </SelectItem>
            <SelectItem value="ready_to_ship" className="text-slate-100">
              Ready to Ship
            </SelectItem>
            <SelectItem value="needs_tracking" className="text-slate-100">
              Needs Tracking
            </SelectItem>
            <SelectItem value="overdue" className="text-slate-100">
              Overdue (3+ days)
            </SelectItem>
          </SelectContent>
        </Select>

        {/* Date Range Filter */}
        <Select value={dateRange} onValueChange={onDateRangeChange}>
          <SelectTrigger className="w-full md:w-[180px] bg-slate-900/70 border-slate-700 text-slate-100">
            <Clock className="h-4 w-4 mr-2 text-slate-500" />
            <SelectValue placeholder="Date range" />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-700">
            <SelectItem value="all" className="text-slate-100">
              All Time
            </SelectItem>
            <SelectItem value="today" className="text-slate-100">
              Today
            </SelectItem>
            <SelectItem value="7days" className="text-slate-100">
              Last 7 Days
            </SelectItem>
            <SelectItem value="30days" className="text-slate-100">
              Last 30 Days
            </SelectItem>
            <SelectItem value="90days" className="text-slate-100">
              Last 90 Days
            </SelectItem>
          </SelectContent>
        </Select>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onClearFilters}
            className="text-slate-400 hover:text-slate-100"
          >
            <X className="h-4 w-4" />
          </Button>
        )}

        {/* Export */}
        <Button
          variant="outline"
          onClick={onExportCSV}
          className="border-slate-700 text-slate-300 hover:bg-slate-800"
        >
          <Download className="h-4 w-4 mr-2" />
          Export CSV
        </Button>
      </div>
    </div>
  );
}